import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { alpha, Box, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import type { ContentRef, InsightRenderer, MetricRenderer, MetricRegistryMeta } from '../types';
import { cardShellSx } from './renderers/rendererTypes';
import { isTileEmptyStatus } from '../utils/tileFetchError';
import TileMetaActions from './TileMetaActions';
import TileErrorSkeleton from './TileErrorSkeleton';

type Props = {
  message: string;
  height: number;
  statusCode?: number | null;
  /** Metric/insight name — API `display_name` or registry label. */
  title?: string;
  /** Superadmin `meta.query` — shown even when the tile is empty. */
  query?: string;
  registry?: MetricRegistryMeta | null;
  renderer?: MetricRenderer | InsightRenderer;
  span?: ContentRef['span'];
};

/** Full-size card for metric/insight fetch failures (error) or 204 empty (neutral). */
export default function TileErrorCard({
  message,
  height,
  statusCode,
  title,
  query,
  registry,
  renderer,
  span,
}: Props) {
  const theme = useTheme();
  const isEmpty = isTileEmptyStatus(statusCode);
  const accent = isEmpty ? theme.palette.grey[500] : theme.palette.error.main;
  const statusColor = isEmpty ? theme.palette.text.secondary : theme.palette.error.dark;
  const barColor = alpha(theme.palette.text.primary, 0.12);
  const statusLabel = isEmpty ? 'No data available' : 'Unable to load';
  const ariaLabel = isEmpty ? 'No data available' : 'Show error details';
  const heading = title?.trim();

  return (
    <Paper
      elevation={0}
      sx={{
        ...cardShellSx(alpha(accent, isEmpty ? 0.45 : 0.35), alpha(accent, isEmpty ? 0.06 : 0.04)),
        minHeight: height,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <TileMetaActions query={query} registry={registry} />
      {heading ? (
        <Typography
          variant="body2"
          sx={{
            position: 'absolute',
            top: 24,
            left: 24,
            right: query?.trim() || registry ? 56 : 24,
            zIndex: 1,
            color: 'text.secondary',
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {heading}
        </Typography>
      ) : null}
      <TileErrorSkeleton
        renderer={renderer}
        span={span}
        hasHeading={Boolean(heading)}
        barColor={barColor}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          px: 2,
          py: 1,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.common.white, 0.45),
        }}
      >
        <ErrorOutlineIcon sx={{ color: accent, fontSize: 28 }} />
        <Typography
          variant="body2"
          sx={{ color: statusColor, fontWeight: 600, textAlign: 'center' }}
        >
          {statusLabel}
        </Typography>
      </Box>

      {!isEmpty ? (
        <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1 }}>
          <Tooltip title={message} arrow>
            <IconButton
              size="small"
              aria-label={ariaLabel}
              sx={{
                color: accent,
                bgcolor: alpha(accent, 0.12),
                '&:hover': { bgcolor: alpha(accent, 0.2) },
                p: 0.75,
              }}
            >
              <ErrorOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Paper>
  );
}
